import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@roger/ui-components';
import { ChevronDown, ChevronUp, Pencil } from 'lucide-react';
import { useStore } from '../../store';
import { NEW_MESSAGE_WINDOW } from '../../store/slices/chatWindows';
import { chatKeys, useUnreadTotal } from '../../hooks/chat/useChatQueries';
import {
  collapseDockAndFocusBar,
  openChatAndFocus,
} from '../../hooks/chat/chatFocus';
import { useOpenChatFromChart } from '../../hooks/chat/useOpenChatFromChart';
import { ChatGlyph } from './ChatGlyph';
import { ChatInboxList } from './ChatInboxList';
import { MOTION } from './chatMotion';
import { ConnectionStrip } from './ConnectionStrip';
import { ManageGroupsMenu } from './ManageGroupsMenu';
import { UnreadPill } from './UnreadPill';

/** The inbox: a 300 x 48 bar when collapsed, a 300 x 540 surface when open. */
export function ChatDock() {
  const dockOpen = useStore(state => state.chatWindows.dockOpen);
  const setChatDockOpen = useStore(state => state.setChatDockOpen);
  const openChatWindow = useStore(state => state.openChatWindow);
  const unread = useUnreadTotal();
  const openFromChart = useOpenChatFromChart();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!dockOpen) return;
    // Data flow: the inbox is refetched on dock open. The 30 s staleTime would
    // otherwise serve the rows fetched when the tray mounted.
    void queryClient.invalidateQueries({ queryKey: chatKeys.inbox() });
  }, [dockOpen, queryClient]);

  if (!dockOpen) {
    return (
      <aside
        role='complementary'
        aria-label='Messages'
        data-chat-dock='collapsed'
        data-chat-surface=''
        className='pointer-events-auto z-40'
      >
        <button
          type='button'
          data-chat-dock-bar=''
          aria-label={
            unread ? `Open messages, ${unread} unread` : 'Open messages'
          }
          aria-expanded={false}
          onClick={openFromChart}
          className='flex h-12 w-[300px] items-center gap-2 rounded-t-[10px] border border-b-0 bg-background px-3 text-left hover:bg-muted'
        >
          <ChatGlyph className='h-5 w-5 text-primary-600' />
          <span className='flex-1 text-sm font-semibold'>Messages</span>
          <UnreadPill count={unread} />
          <ChevronUp className='h-4 w-4 text-muted-foreground' />
        </button>
      </aside>
    );
  }

  return (
    <aside
      role='complementary'
      aria-label='Messages'
      data-chat-dock='open'
      data-chat-surface=''
      // The collapsed and open `aside` are the same element to React, so the
      // class lands on open and the animation runs then.
      className={`pointer-events-auto z-40 flex h-[540px] max-h-[calc(100vh-80px)] w-[300px] flex-col overflow-hidden rounded-t-[10px] border border-b-0 bg-background ${MOTION.window}`}
    >
      <div className='flex h-[52px] shrink-0 items-center gap-1 border-b pl-3 pr-2'>
        <h2 className='text-sm font-semibold'>Messages</h2>
        <UnreadPill count={unread} />
        <div className='ml-auto flex items-center gap-1'>
          <Button
            size='icon'
            variant='ghost'
            aria-label='New message'
            title='New message'
            onClick={() => openChatWindow(NEW_MESSAGE_WINDOW)}
          >
            <Pencil className='h-3.5 w-3.5' />
          </Button>
          <ManageGroupsMenu
            size='icon'
            onNavigate={() => setChatDockOpen(false)}
          />
          <Button
            size='icon'
            variant='ghost'
            aria-label='Collapse messages'
            title='Collapse messages'
            onClick={collapseDockAndFocusBar}
          >
            <ChevronDown className='h-3.5 w-3.5' />
          </Button>
        </div>
      </div>
      <ConnectionStrip />
      <ChatInboxList
        density='compact'
        onOpen={openChatAndFocus}
        onNewMessage={() => openChatWindow(NEW_MESSAGE_WINDOW)}
      />
    </aside>
  );
}
